import { useEffect, useState } from "react";
import { UploadOutlined } from "@ant-design/icons";
import { useCreate } from "@refinedev/core";
import { Button, Form, Input, message, Modal, Upload, UploadFile } from "antd";
import { v4 as randomId } from "uuid";
import { EFileTypes } from "components";
import { AntiDopingMaterial, AntiDopingMaterialFormValues } from "./types";

type AntiDopingMaterialModalProps = {
  open: boolean;
  saving: boolean;
  material?: AntiDopingMaterial;
  onCancel: () => void;
  onSubmit: (values: AntiDopingMaterialFormValues) => Promise<void>;
};

export const AntiDopingMaterialModal = ({
  open,
  saving,
  material,
  onCancel,
  onSubmit,
}: AntiDopingMaterialModalProps) => {
  const [form] = Form.useForm<AntiDopingMaterialFormValues>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const { mutate: uploadFile, isLoading: uploading } = useCreate();

  useEffect(() => {
    if (!open) {
      return;
    }

    form.setFieldsValue({
      id: material?.id,
      fileName: material?.fileName || "",
      linkUrl: material?.linkUrl || "",
      linkType: "FILE",
      clickableWholeBlock: material?.clickableWholeBlock ?? false,
      ord: material?.ord,
    });
    setFileList(
      material?.linkUrl
        ? [
            {
              uid: String(material.id),
              name: material.fileName || material.linkUrl.replace(/.*\//, ""),
              url: material.linkUrl,
            },
          ]
        : []
    );
  }, [form, material, open]);

  const handleSubmit = async () => {
    let values: AntiDopingMaterialFormValues;

    try {
      values = await form.validateFields();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }

      throw error;
    }

    if (!values.linkUrl) {
      message.error("Загрузите файл");
      return;
    }

    await onSubmit({
      ...values,
      linkType: "FILE",
    });
    form.resetFields();
    setFileList([]);
  };

  return (
    <Modal
      title={material ? "Редактировать материал" : "Добавить материал"}
      open={open}
      okText="Сохранить"
      cancelText="Отмена"
      confirmLoading={saving}
      onOk={handleSubmit}
      onCancel={onCancel}
      destroyOnClose
    >
      <Form<AntiDopingMaterialFormValues>
        form={form}
        layout="vertical"
        initialValues={{
          linkType: "FILE",
          clickableWholeBlock: false,
        }}
      >
        <Form.Item name="id" hidden>
          <Input />
        </Form.Item>

        <Form.Item
          name="fileName"
          label="Название файла"
          rules={[{ required: true, message: "Укажите название" }]}
        >
          <Input placeholder="Название файла" />
        </Form.Item>

        <Form.Item label="Загрузить файл">
          <Upload
            customRequest={(request) => {
              uploadFile(
                {
                  dataProviderName: "uploadDataProvider",
                  resource: "",
                  values: {
                    entityId: `medicine/anti-doping/materials/${randomId()}`,
                    serviceType: EFileTypes.DOCUMENTS,
                    blob: request.file as File,
                    type: "DOCUMENTS",
                  },
                },
                {
                  onSuccess: (data: any) => {
                    const fileName = (request.file as File).name;
                    const fileUrl =
                      data?.data?.s3id ||
                      data?.data?.url ||
                      data?.s3id ||
                      data?.url;

                    if (!fileUrl) {
                      request.onError?.(
                        new Error("Не удалось получить ссылку на файл")
                      );
                      return;
                    }

                    form.setFieldValue("linkUrl", fileUrl);
                    if (!form.getFieldValue("fileName")) {
                      form.setFieldValue("fileName", fileName);
                    }
                    form.setFieldValue("linkType", "FILE");
                    request.onSuccess?.("ok");
                  },
                  onError: (error: any) => {
                    request.onError?.(error);
                  },
                }
              );
            }}
            fileList={fileList}
            onChange={({ fileList }) => {
              setFileList(fileList);
              if (!fileList.length) {
                form.setFieldValue("linkUrl", undefined);
              }
            }}
          >
            <Button
              disabled={!!fileList.length}
              loading={uploading}
              icon={<UploadOutlined />}
            >
              Загрузить файл
            </Button>
          </Upload>
        </Form.Item>

        <Form.Item name="linkUrl" hidden>
          <Input />
        </Form.Item>

        <Form.Item name="linkType" hidden initialValue="FILE">
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};
